import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { PrismButton, PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { and, eq, not } from 'ember-truth-helpers';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import type { LookerDashboard } from 'lumen/models';

interface MarketCustomReportingSidebarDashboardSignature {
  Args: {
    dashboard: LookerDashboard;
    level: number;
    selectedDashboard?: LookerDashboard | null;
    type?: string;
    viewOnly: boolean;
  };
}

export default class MarketCustomReportingSidebarDashboard extends Component<MarketCustomReportingSidebarDashboardSignature> {
  @service declare access: Services['access'];
  @service declare customReporting: Services['custom-reporting'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get editOptions() {
    const isScheduledDisabled = [
      'market.custom-reporting.dashboard.edit',
      'destination.dashboard.edit',
    ].includes(this.router.currentRouteName!);

    if (this.isTemplate) {
      return [
        {
          id: 'schedule',
          label: this.intl.t('custom_reporting.schedule_report'),
          disabled: isScheduledDisabled,
        },
        { id: 'copy', label: this.intl.t('custom_reporting.actions.copy') },
      ] as const;
    }

    return [
      {
        id: 'schedule',
        label: this.intl.t('custom_reporting.schedule_report'),
        disabled: isScheduledDisabled,
      },
      { id: 'move', label: this.intl.t('custom_reporting.actions.move') },
      { id: 'copy', label: this.intl.t('custom_reporting.actions.copy') },
      { id: 'rename', label: this.intl.t('custom_reporting.actions.rename') },
      { id: 'delete', label: this.intl.t('custom_reporting.actions.delete') },
    ] as const;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isTemplate() {
    const { type, dashboard } = this.args;

    if (!dashboard.folder.canEdit) {
      return true;
    }

    return type === 'templates' || !this.access.hasCustomReportingEdit;
  }

  @action onClick(editMode = false): void {
    this.customReporting.loadDashboard(this.args.dashboard, editMode);
  }

  @action onEditOptionSelect(option: (typeof this.editOptions)[number]): void {
    const { dashboard } = this.args;

    switch (option.id) {
      case 'delete':
        this.customReporting.deleteDashboard(dashboard);
        break;
      case 'rename':
        this.customReporting.editDashboard(dashboard);
        break;
      case 'copy':
        this.customReporting.copyDashboard(dashboard);
        break;
      case 'move':
        this.customReporting.moveDashboard(dashboard);
        break;
      case 'schedule': {
        this.scheduleReport();
        break;
      }
      default:
        option satisfies never;
    }
  }

  @action scheduleReport(): void {
    const { dashboard, selectedDashboard } = this.args;

    this.customReporting.pendingScheduleRequest = true;

    if (selectedDashboard?.id === dashboard.id) {
      this.customReporting.triggerSchedule();
      return;
    }

    this.customReporting.loadDashboard(dashboard, false);
  }

  <template>
    <div
      class="sidebar-item text-300--regular dashboard-item flex items-center gap-200 pr-100 pl-200
        {{if
          (and (eq @dashboard.id @selectedDashboard.id) (not @viewOnly))
          'sidebar-item--active'
        }}
        {{if @viewOnly 'disabled'}}"
      data-test-id={{concat "dashboard-" @dashboard.id}}
      style={{htmlSafe (concat "--level:" @level)}}
    >
      <button
        class="button--unstyled dashboard-button flex w-full flex-row items-center"
        disabled={{@viewOnly}}
        title={{@dashboard.title}}
        type="button"
        {{on "click" (fn this.onClick false)}}
      >
        <span class="truncate">
          {{@dashboard.title}}
        </span>
      </button>
      {{#unless @viewOnly}}
        <div class="actions flex flex-row gap-100">
          {{#if this.access.hasCustomReportingEdit}}
            {{! @TODO vfuto - replace this to prism/button once it's in the type registry }}
            {{#let
              (component
                GeneralDropdownsTriggersTriggerDropdownIconButton
                buttonType="ghost"
                icon="meatballs"
                small=true
              )
              as |triggerComponent|
            }}
              <PrismComboSelectInput
                @horizontalPosition="left"
                @onChange={{this.onEditOptionSelect}}
                @options={{this.editOptions}}
                @unsafe_dropdownClass={{unless
                  this.isTemplate
                  "custom-reporting--actions-dropdown"
                }}
                @unsafe_triggerComponent={{triggerComponent}}
                data-test-id="edit-options"
              />
            {{/let}}
          {{else}}
            <PrismButton
              @buttonType="ghost"
              @icon="calendar-clock"
              @onClick={{this.scheduleReport}}
              @size="small"
              data-test-id="schedule-report"
            />
          {{/if}}
          <PrismButton
            @buttonType="ghost"
            @icon={{if this.isTemplate "visibility-on" "edit-pencil"}}
            @onClick={{fn this.onClick (not this.isTemplate)}}
            @size="small"
            data-test-id={{if
              this.isTemplate
              "view-dashboard"
              "edit-dashboard"
            }}
          />
        </div>
      {{/unless}}
    </div>
  </template>
}
